import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  User, 
  LogOut, 
  ChevronRight, 
  Loader2,
  Crown,
  Mic,
  Square,
  Check,
  Clock,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { getProfile, updateProfile, logout, completeOnboarding, transcribeAudio, getMe } from '../lib/api';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

const PROFILE_FIELDS = [
  { 
    id: 'life_situation', 
    label: 'Life Situation', 
    placeholder: 'Share about where you are in life right now...',
    voicePrompt: 'Tell me about your current life situation'
  },
  { 
    id: 'relationships', 
    label: 'Important Relationships', 
    placeholder: 'Key people in your life...',
    voicePrompt: 'Describe the important relationships in your life'
  },
  { 
    id: 'career', 
    label: 'Work & Purpose', 
    placeholder: 'What occupies your days and drives you...',
    voicePrompt: 'Tell me about your work or what drives you'
  },
  { 
    id: 'goals', 
    label: 'Dreams & Goals', 
    placeholder: 'What are you working toward...',
    voicePrompt: 'Share your dreams and goals'
  },
];

function VoiceInput({ fieldId, onTranscription, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await handleTranscription(audioBlob);
        }
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };
  
  const handleTranscription = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const response = await transcribeAudio(audioBlob);
      onTranscription(response.data.text);
      toast.success('Voice captured!');
    } catch (error) {
      console.error('Transcription failed:', error);
      toast.error('Failed to transcribe. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <button
      type="button"
      onClick={isRecording ? stopRecording : startRecording}
      disabled={disabled || isTranscribing}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
        isRecording 
          ? 'bg-red-500 text-white recording-pulse' 
          : isTranscribing
          ? 'bg-muted text-muted-foreground'
          : 'bg-primary/20 text-primary hover:bg-primary/30'
      }`}
      data-testid={`voice-btn-${fieldId}`}
    >
      {isTranscribing ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Transcribing...</span>
        </>
      ) : isRecording ? (
        <>
          <Square className="w-4 h-4" />
          <span>{formatTime(recordingTime)}</span>
        </>
      ) : (
        <>
          <Mic className="w-4 h-4" />
          <span>Speak</span>
        </>
      )}
    </button>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = location.state?.onboarding || false;
  
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState({
    life_situation: '',
    relationships: '',
    career: '',
    goals: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user with subscription info
        const meResponse = await getMe();
        setUser(meResponse.data);
        setSubscription(meResponse.data.subscription);
        localStorage.setItem('onirva_user', JSON.stringify(meResponse.data));
        
        // Fetch profile
        const profileResponse = await getProfile();
        setProfile({
          life_situation: profileResponse.data.life_situation || '',
          relationships: profileResponse.data.relationships || '',
          career: profileResponse.data.career || '',
          goals: profileResponse.data.goals || '',
        });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handleVoiceTranscription = (field, text) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field] ? `${prev[field]} ${text}` : text,
    }));
    setHasChanges(true);
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile(profile);
      
      if (isOnboarding) {
        await completeOnboarding();
        const updatedUser = { ...user, has_completed_onboarding: true };
        localStorage.setItem('onirva_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        toast.success('Profile saved! Welcome to ONIRVA!');
        navigate('/dashboard');
      } else {
        setHasChanges(false);
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSkipOnboarding = async () => {
    setIsSaving(true);
    try {
      await completeOnboarding();
      const updatedUser = { ...user, has_completed_onboarding: true };
      localStorage.setItem('onirva_user', JSON.stringify(updatedUser));
      toast.success('Welcome to ONIRVA!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to continue');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('onirva_token');
      localStorage.removeItem('onirva_user');
      navigate('/');
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="profile-page">
      <div className="hero-gradient absolute inset-0" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 pt-8">
          {isOnboarding ? (
            <>
              <div className="flex justify-center mb-4">
                <img src={ONIRVA_ICON} alt="ONIRVA" className="w-16 h-16 rounded-2xl" />
              </div>
              <h1 className="text-2xl font-serif font-bold mb-1 text-center">Your Life Context</h1>
              <p className="text-muted-foreground text-sm text-center">
                Share only what feels relevant. This is optional and can be updated anytime.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-serif font-bold mb-1">Profile</h1>
              <p className="text-muted-foreground text-sm">Your personal context for dream reflection</p>
            </>
          )}
        </header>
        
        {/* User Info Card - Only show when not onboarding */}
        {!isOnboarding && (
          <div className="px-6 mb-6">
            <div className="glass-card p-4 flex items-center gap-4">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
              )}
              <div className="flex-1">
                <h2 className="font-bold">{user?.name || 'Dreamer'}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Subscription Card - Only show when not onboarding */}
        {!isOnboarding && (
          <div className="px-6 mb-6">
            <div 
              className="glass-card p-4 bg-gradient-to-r from-primary/20 to-violet-500/20 border-primary/30 cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => navigate('/subscription')}
              data-testid="subscription-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center">
                    {subscription?.status === 'trial' ? (
                      <Clock className="w-5 h-5 text-primary" />
                    ) : subscription?.tier === 'deep' ? (
                      <Crown className="w-5 h-5 text-amber-400" />
                    ) : subscription?.tier === 'plus' ? (
                      <Sparkles className="w-5 h-5 text-primary" />
                    ) : (
                      <Crown className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div>
                    {subscription?.status === 'trial' ? (
                      <>
                        <h3 className="font-bold">Free Trial</h3>
                        <p className="text-xs text-muted-foreground">
                          {subscription.trial_days_remaining} days remaining • ONIRVA Plus
                        </p>
                      </>
                    ) : subscription?.tier === 'plus' || subscription?.tier === 'deep' ? (
                      <>
                        <h3 className="font-bold">{subscription.tier_name}</h3>
                        <p className="text-xs text-muted-foreground">Active subscription</p>
                      </>
                    ) : (
                      <>
                        <h3 className="font-bold">Subscription</h3>
                        <p className="text-xs text-muted-foreground">
                          {subscription?.analyses_remaining !== undefined 
                            ? `${subscription.analyses_remaining} analyses remaining` 
                            : 'View options'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
          </div>
        )}
        
        {/* Onboarding Info Card */}
        {isOnboarding && (
          <div className="px-6 mb-6">
            <div className="glass-card p-4">
              <div className="flex items-start gap-3">
                <Mic className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium mb-1">Voice Input Available</h3>
                  <p className="text-sm text-muted-foreground">
                    Tap "Speak" next to each field to record your answer. It's faster than typing!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Profile Fields */}
        <div className="px-6 space-y-6">
          {!isOnboarding && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <img src={ONIRVA_ICON} alt="" className="w-6 h-6 rounded-lg" />
                <h2 className="font-serif font-bold">Life Context</h2>
              </div>
              
              <p className="text-sm text-muted-foreground -mt-4 mb-4">
                Share only what feels relevant. This is optional and can be updated anytime.
              </p>
            </>
          )}
          
          {PROFILE_FIELDS.map((field) => (
            <div key={field.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor={field.id} className="text-muted-foreground">{field.label}</Label>
                <VoiceInput 
                  fieldId={field.id}
                  onTranscription={(text) => handleVoiceTranscription(field.id, text)}
                  disabled={isSaving}
                />
              </div>
              <Textarea
                id={field.id}
                placeholder={field.placeholder}
                value={profile[field.id]}
                onChange={(e) => handleChange(field.id, e.target.value)}
                className="min-h-[100px] bg-muted/50 border-border resize-none"
                data-testid={`profile-${field.id}-input`}
              />
            </div>
          ))}
          
          {/* Save Button */}
          <Button
            className="w-full rounded-full py-6"
            onClick={handleSave}
            disabled={(!hasChanges && !isOnboarding) || isSaving}
            data-testid="save-profile-btn"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : isOnboarding ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save & Continue
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
          
          {/* Skip Button for Onboarding */}
          {isOnboarding && (
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={handleSkipOnboarding}
              disabled={isSaving}
            >
              Skip for now
            </Button>
          )}
          
          {/* Logout Button - Only show when not onboarding */}
          {!isOnboarding && (
            <Button
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
